import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"

import { useLazyGetTableColumnsQuery } from "store/api/dashboardApi"
import { useLazyGetVendorFulfilledOrdersQuery, useLazyGetVendorOrdersQuery } from "store/api/vendorApi"
import { useAppDispatch, useAppSelector } from "store/hooks"
import { setCurrentOrder, setSelectedVSMOrder } from "store/tempDataSlise"
import { orderStatusOptions } from "constants/SelectFieldOptions"
import { UITypes } from "types"

import { convertVSMOrdersToTableRows } from "utils/convertData"
import getErrorMessage from "utils/getErrorMessage"
import { reorderTableHeaderCells } from "utils/reorderTabbleColumns"
import useExportCSV from "hooks/useExportCSV"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { DeleteOrderModalForVendor } from "modals/DeleteOrderModalForVendor"
import { EditVendorOrderModal } from "modals/EditVendorOrderModal"
import { NewVendorOrderModal } from "modals/NewVendorOrderModal"
import { OrderFulfillmentModal } from "modals/OrderFulfillmentModal"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import EditColumnsButton from "components/EditColumnsButton"
import { ButtonNew } from "components/form/ButtonNew"
import { CheckboxNew } from "components/form/CheckboxNew"
import { StatusOfOrder } from "components/table/columnComponents/StatusOfOrder"
import TableWrapper from "components/table/TableWrapper"
import { WidgetVendorOrderStatus } from "components/widgets/WidgetVendorOrderStatus"
import { WidgetWarehouseInformation } from "components/widgets/WidgetWarehouseInformation"

import styles from "./index.module.scss"

const initialTableHeaderCells: UITypes.TableHeaderCell[] = [
  {
    title: "",
    name: "checkbox",
    cellComponent: "checkbox",
    size: "mini",
  },
  {
    title: "Order number",
    type: "sort-search",
    name: "id",
  },
  {
    title: "Status",
    type: "select",
    name: "status",
    options: orderStatusOptions,
    cellComponent: StatusOfOrder,
  },
  {
    title: "Company name",
    type: "sort-search",
    name: "company_name",
  },
  {
    title: "Creation Date",
    type: "sort-date-range",
    name: "create_date",
  },
  {
    title: "Shipping date",
    type: "date-range",
    name: "shipping_date",
  },
  {
    title: "Fulfillment date",
    type: "date-range",
    name: "fulfillment_date",
  },
  {
    title: "Quantity",
    type: "sort-search",
    name: "quantity",
  },
  {
    title: "Warehouse",
    type: "sort-search",
    name: "warehouse",
  },
  {
    title: "Phone number",
    type: "sort-search",
    name: "phone_number",
  },
  {
    title: "Email",
    type: "sort-search",
    name: "email",
  },
  {
    title: "Status Updated",
    type: "sort-search",
    name: "status_updated",
  },
]

export const Orders = () => {
  const dispatch = useAppDispatch()
  const [showNewVendorOrderModal, setShowNewVendorOrderModal] = useState(false)
  const [showDeleteOrderModalForVendor, setShowDeleteOrderModalForVendor] = useState(false)
  const [triggerGetOrders, { isFetching, data }] = useLazyGetVendorOrdersQuery()
  const [triggerGetFulfilledOrder, { isFetching: isFetchingFulfilled, data: dataFulfilled }] =
    useLazyGetVendorFulfilledOrdersQuery()
  const [triggerGetTableColumns, { data: columnsData }] = useLazyGetTableColumnsQuery()
  const [selectFulfilled, setSelectFulfilled] = useState<boolean>(false)
  const selectedVSMOrder = useAppSelector(state => state.tempData.selectedVSMOrder)
  const { urlQueryParams } = useUrlQueryParams()
  const [isFirstRender, setFirstRender] = useState(true)
  const [showOrderFulfillmentModal, setShowOrderFulfillmentModal] = useState(false)
  const [showEditVendorOrderModal, setShowEditVendorOrderModal] = useState(false)
  const meta = selectFulfilled ? dataFulfilled?.meta : data?.meta
  const [updateIndex, setUpdateIndex] = useState(0)

  const tableHeaderCells = useMemo(
    (): UITypes.TableHeaderCell[] =>
      columnsData?.data?.config
        ? reorderTableHeaderCells(initialTableHeaderCells, columnsData?.data?.config)
        : initialTableHeaderCells,
    [columnsData],
  )

  const filteredTableHeaderCells = useMemo(() => {
    return tableHeaderCells.filter(item => selectFulfilled || item.name !== "fulfillment_date")
  }, [tableHeaderCells, selectFulfilled])

  const { handleExport } = useExportCSV({
    params: urlQueryParams,
    headerCells: filteredTableHeaderCells,
    meta: meta,
    fileName: "orders",
    convertData: convertVSMOrdersToTableRows,
    useLazyQuery: selectFulfilled ? useLazyGetVendorFulfilledOrdersQuery : useLazyGetVendorOrdersQuery,
  })

  const onSetCurrentOrder = (id?: number) => {
    if (!selectFulfilled) {
      const ordersList = data?.data

      if (!id || !ordersList?.length) {
        dispatch(setSelectedVSMOrder(undefined))
        dispatch(setCurrentOrder(undefined))
      } else {
        const selectedVSMOrder = ordersList.filter(item => item.id === id)[0]
        dispatch(setSelectedVSMOrder(selectedVSMOrder))
        dispatch(setCurrentOrder(selectedVSMOrder))
      }
    }
  }

  const onEditClick = (id: number) => {
    if (!selectFulfilled) {
      onSetCurrentOrder(id)
      setShowEditVendorOrderModal(true)
    }
  }

  const onDeleteClick = (id: number) => {
    if (!selectFulfilled) {
      onSetCurrentOrder(id)
      setShowDeleteOrderModalForVendor(true)
    }
  }

  const onNewClick = () => {
    setShowNewVendorOrderModal(true)
  }

  const onShowFulfill = (event: React.FormEvent<HTMLInputElement>) => {
    setSelectFulfilled(event.currentTarget.checked)
  }

  const onFulfillClick = () => {
    if (!checkFulfillAbility()) return false
    setShowOrderFulfillmentModal(true)
  }

  const onCheckboxChange = (id: number) => {
    onSetCurrentOrder(id === selectedVSMOrder?.id ? undefined : id)
  }

  const checkFulfillAbility = () => {
    return selectedVSMOrder?.id && selectedVSMOrder?.status === "shipping"
  }

  const getOrders = async (params: UITypes.Params) => {
    return selectFulfilled ? await triggerGetFulfilledOrder(params) : await triggerGetOrders(params)
  }

  const handleActionSuccess = () => {
    setShowNewVendorOrderModal(false)
    setShowOrderFulfillmentModal(false)
    setShowEditVendorOrderModal(false)
    setShowDeleteOrderModalForVendor(false)
    setUpdateIndex(prev => prev + 1)
    getOrders(urlQueryParams)
  }

  const sendRequest = async (params: UITypes.Params) => {
    try {
      const res = await getOrders(params)
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (isFirstRender) {
      setFirstRender(false)
      return
    }
    sendRequest(urlQueryParams)
    triggerGetTableColumns({ list_name: "admin_orders_list" })
  }, [urlQueryParams, selectFulfilled, isFirstRender])

  return (
    <>
      <div className={styles.widgetsWrapper}>
        <WidgetWarehouseInformation updateIndex={updateIndex} />
        <WidgetVendorOrderStatus updateIndex={updateIndex} />
      </div>
      <ContentWrapper title="Orders history">
        <nav className={styles.tableNavWrapper}>
          <div>
            <ButtonNew onClick={onNewClick} color="blue">
              New order
            </ButtonNew>
          </div>
          <div className={styles.tableNavButtonsWrapper}>
            <EditColumnsButton tableName="admin_orders_list" tableHeaderCells={tableHeaderCells} />
            <ButtonNew
              disabled={!checkFulfillAbility()}
              onClick={onFulfillClick}
              color={!checkFulfillAbility() ? "grey" : "blue"}
            >
              Fulfill
            </ButtonNew>
            <CheckboxNew onChange={onShowFulfill} className={styles.tableNavItem} isReverse={true} id="fulfilled">
              Show fulfilled
            </CheckboxNew>
            <ButtonNew onClick={handleExport} className={styles.tableNavItem} disabled={meta?.total === 0}>
              Export
            </ButtonNew>
          </div>
        </nav>
        <TableWrapper
          tableName="vendor-orders"
          headerCells={filteredTableHeaderCells}
          columnsLoaded={!!columnsData}
          data={convertVSMOrdersToTableRows(selectFulfilled ? dataFulfilled?.data : data?.data)}
          isLoading={isFetching || isFetchingFulfilled}
          currentItemID={selectedVSMOrder?.id}
          onCheckboxChange={onCheckboxChange}
          onEditClick={onEditClick}
          onDeleteClick={onDeleteClick}
          clearAdditionalTags={() => dispatch(setSelectedVSMOrder(undefined))}
          additionalTags={
            selectedVSMOrder
              ? [
                  {
                    label: "Order number",
                    values: [selectedVSMOrder.id],
                  },
                ]
              : []
          }
        />
        <Pagination meta={meta} />
      </ContentWrapper>
      {showOrderFulfillmentModal && (
        <OrderFulfillmentModal
          isVendor
          onCancel={() => setShowOrderFulfillmentModal(false)}
          onSuccess={handleActionSuccess}
        />
      )}
      {showNewVendorOrderModal && (
        <NewVendorOrderModal onCancel={() => setShowNewVendorOrderModal(false)} onSuccess={handleActionSuccess} />
      )}
      {showEditVendorOrderModal && (
        <EditVendorOrderModal onCancel={() => setShowEditVendorOrderModal(false)} onSuccess={handleActionSuccess} />
      )}
      {showDeleteOrderModalForVendor && (
        <DeleteOrderModalForVendor
          onCancel={() => setShowDeleteOrderModalForVendor(false)}
          onSuccess={handleActionSuccess}
        />
      )}
    </>
  )
}
